import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Box, Chip, Link, Typography } from "@mui/material";
import { format } from "date-fns";
import { baseTheme } from "../../../themes/shipinsure";
import { formatMoney } from "../getStatusViewModel";
import { getTrackingUrl } from "../getTrackingUrl";

export function ResolutionPanel({ resolution, t }) {
  if (resolution.type === "refund") {
    return (
      <PanelShell title={t("status.resolutionPanel.refundAmount")}>
        <Typography variant="h4" fontWeight={700} mb="8px">
          {formatMoney(resolution.amount, resolution.currency)}
        </Typography>
        <Chip
          label={t("status.resolutionPanel.refundProcessing")}
          sx={{ mb: "16px", backgroundColor: "#FFF8E6", color: "#8A6100" }}
        />
        {resolution.issuedAt ? (
          <DetailRow
            label={t("status.resolutionPanel.refundIssuedOn")}
            value={format(new Date(resolution.issuedAt), "MMMM dd, yyyy")}
          />
        ) : null}
        <Box
          mt="16px"
          sx={{
            backgroundColor: baseTheme.colors.secondary,
            borderRadius: "12px",
            padding: "12px 16px",
          }}
        >
          <Typography fontSize="14px">
            {t("status.resolutionPanel.refundTiming")}
          </Typography>
        </Box>
      </PanelShell>
    );
  }

  if (resolution.type === "store_credit") {
    return (
      <PanelShell title={t("status.resolutionPanel.storeCreditAmount")}>
        <Typography variant="h4" fontWeight={700} mb="8px">
          {formatMoney(resolution.amount, resolution.currency)}
        </Typography>
        {resolution.discountCode ? (
          <DetailRow
            label={t("status.resolutionPanel.storeCreditCode")}
            value={resolution.discountCode}
          />
        ) : null}
        {resolution.issuedAt ? (
          <DetailRow
            label={t("status.resolutionPanel.refundIssuedOn")}
            value={format(new Date(resolution.issuedAt), "MMMM dd, yyyy")}
          />
        ) : null}
        <Box
          mt="16px"
          sx={{
            backgroundColor: "#EEFBF1",
            borderRadius: "12px",
            padding: "12px 16px",
          }}
        >
          <Typography fontSize="14px">
            {t("status.resolutionPanel.storeCreditHowTo")}
          </Typography>
        </Box>
      </PanelShell>
    );
  }

  if (resolution.type === "reship") {
    const shipment = resolution.shipment;
    const trackingUrl =
      getTrackingUrl(resolution.reshipShipInsureId) ||
      shipment?.tracking_url ||
      null;

    return (
      <PanelShell title={t("status.resolutionPanel.reshipStatus")}>
        {!shipment?.tracking_number ? (
          <>
            <Chip
              label={t("status.resolutionPanel.reshipPending")}
              sx={{ mb: "16px", backgroundColor: "#F3E8FF", color: "#5B21B6" }}
            />
            <Typography fontSize="14px" color="#1E293B">
              {t("status.resolutionPanel.reshipNotAvailable")}
            </Typography>
            {trackingUrl ? <TrackingLink href={trackingUrl} t={t} /> : null}
          </>
        ) : (
          <>
            <DetailRow
              label={t("status.resolutionPanel.reshipTracking")}
              value={shipment.tracking_number}
              href={trackingUrl}
            />
            {shipment.carrier ? (
              <DetailRow
                label={t("status.resolutionPanel.reshipCarrier")}
                value={shipment.carrier}
              />
            ) : null}
            {shipment.estimated_delivery_date ? (
              <DetailRow
                label={t("status.resolutionPanel.reshipEta")}
                value={format(
                  new Date(shipment.estimated_delivery_date),
                  "MMMM dd, yyyy",
                )}
              />
            ) : null}
            {trackingUrl ? <TrackingLink href={trackingUrl} t={t} /> : null}
          </>
        )}
      </PanelShell>
    );
  }

  if (resolution.phase !== "resolution") {
    return null;
  }

  return (
    <PanelShell title={t("status.resolutionPanel.pendingTitle")} emphasized>
      <Typography fontSize="14px" color="#2F4668">
        {t("status.resolutionPanel.pendingDescription", {
          resolution: resolution.preferredResolution || "resolution",
        })}
      </Typography>
    </PanelShell>
  );
}

function TrackingLink({ href, t }) {
  return (
    <Link
      href={href}
      target="_blank"
      rel="noreferrer"
      underline="hover"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        mt: "8px",
        fontSize: "14px",
        fontWeight: 600,
        color: baseTheme.colors.primary,
      }}
    >
      {t("status.viewTracking")}
      <OpenInNewIcon sx={{ fontSize: 14 }} />
    </Link>
  );
}

function PanelShell({ title, children, emphasized = false }) {
  return (
    <Box
      mb="16px"
      sx={{
        border: emphasized
          ? `1px solid ${baseTheme.colors.primary}`
          : "1px solid #C5D0E0",
        borderRadius: "12px",
        padding: "16px",
        backgroundColor: emphasized ? "#F7F8FF" : baseTheme.colors.neutral,
      }}
    >
      <Typography fontWeight={700} mb="8px" fontSize="1.05rem">
        {title}
      </Typography>
      {children}
    </Box>
  );
}

function DetailRow({ label, value, href }) {
  return (
    <Box mb="8px">
      <Typography fontSize="14px" fontWeight={700} color="#1E293B">
        {label}
      </Typography>
      {href ? (
        <Link
          href={href}
          target="_blank"
          rel="noreferrer"
          underline="hover"
          sx={{
            fontSize: "15px",
            fontWeight: 600,
            color: baseTheme.colors.primary,
            wordBreak: "break-all",
          }}
        >
          {value}
        </Link>
      ) : (
        <Typography fontSize="15px">{value}</Typography>
      )}
    </Box>
  );
}
